import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const AuthContext = createContext();

// Session idles out after 2 hours of inactivity
const INACTIVITY_LIMIT_MS = 2 * 60 * 60 * 1000;
const ACTIVITY_KEY = 'last_activity_at';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [impersonatedBy, setImpersonatedBy] = useState(null); // { id, first_name, last_name, email } or null
  const timeoutRef = useRef(null);

  const clearSessionStorage = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem(ACTIVITY_KEY);
    sessionStorage.removeItem('impersonator_user');
    sessionStorage.removeItem('impersonator_token');
    sessionStorage.removeItem('impersonator_admin');
  };

  const handleTimeout = useCallback(() => {
    setUser(null);
    setToken(null);
    clearSessionStorage();
    setSessionExpired(true);
  }, []);

  const touchActivity = useCallback(() => {
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(handleTimeout, INACTIVITY_LIMIT_MS);
  }, [handleTimeout]);

  // Restore session on mount, respecting the inactivity window
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);

    if (storedUser && storedToken) {
      if (lastActivity && Date.now() - lastActivity > INACTIVITY_LIMIT_MS) {
        // Already expired — do not restore
        clearSessionStorage();
        setSessionExpired(true);
      } else {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
        touchActivity();
        // Restore impersonation banner if there is a parked admin session
        const parkedAdmin = sessionStorage.getItem('impersonator_admin');
        if (parkedAdmin) {
          try { setImpersonatedBy(JSON.parse(parkedAdmin)); } catch (_e) {}
        }
      }
    }
    setLoading(false);
  }, [touchActivity]);

  // Bind user activity listeners when logged in
  useEffect(() => {
    if (!token) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return undefined;
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handler = () => touchActivity();
    events.forEach((ev) => window.addEventListener(ev, handler, { passive: true }));

    // Periodic check (every 60s) in case setTimeout was throttled in a background tab
    const intv = setInterval(() => {
      const last = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
      if (last && Date.now() - last > INACTIVITY_LIMIT_MS) {
        handleTimeout();
      }
    }, 60_000);

    // Cross-tab sync: if another tab logs out or expires, this one follows
    const onStorage = (e) => {
      if (e.key === 'token' && !e.newValue) {
        setUser(null);
        setToken(null);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handler));
      window.removeEventListener('storage', onStorage);
      clearInterval(intv);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [token, touchActivity, handleTimeout]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setSessionExpired(false);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    touchActivity();
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearSessionStorage();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const dismissSessionExpired = () => setSessionExpired(false);

  /**
   * Start impersonating a user. Parks the current admin session in sessionStorage
   * so we can revert later. Replaces the active token/user with the impersonation
   * payload and shows the banner.
   */
  const startImpersonation = ({ token: impToken, user: impUser, admin }) => {
    // Park the admin session
    if (token && user) {
      sessionStorage.setItem('impersonator_token', token);
      sessionStorage.setItem('impersonator_user', JSON.stringify(user));
      sessionStorage.setItem('impersonator_admin', JSON.stringify(admin || user));
    }
    setImpersonatedBy(admin || user);
    setUser(impUser);
    setToken(impToken);
    setSessionExpired(false);
    localStorage.setItem('user', JSON.stringify(impUser));
    localStorage.setItem('token', impToken);
    touchActivity();
  };

  /**
   * Restore the parked admin session. If no parked session is found, falls back
   * to a clean logout (forces re-login).
   */
  const stopImpersonation = () => {
    const parkedToken = sessionStorage.getItem('impersonator_token');
    const parkedUser = sessionStorage.getItem('impersonator_user');
    if (parkedToken && parkedUser) {
      const adminUser = JSON.parse(parkedUser);
      setUser(adminUser);
      setToken(parkedToken);
      localStorage.setItem('user', parkedUser);
      localStorage.setItem('token', parkedToken);
      sessionStorage.removeItem('impersonator_token');
      sessionStorage.removeItem('impersonator_user');
      sessionStorage.removeItem('impersonator_admin');
      setImpersonatedBy(null);
      touchActivity();
    } else {
      // No parked session — force a fresh login
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout, updateUser,
      sessionExpired, dismissSessionExpired,
      impersonatedBy, startImpersonation, stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
